import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { trpClient } from '../client/trpClient';
import { dashboardClient, DashboardSummary } from '../client/dashboardClient';
import { useAuth } from '../contexts/AuthContext';
import {
    DashboardRFQ, DashboardKpis, RFQStage, OwnerView, StageFilter,
    PAGE_LIMIT, isActiveRfq, needsBuyerReview, mapRfq, deriveAttention, deriveKpisFromPage,
} from './dashboardMappers';

// Shared dashboard vocabulary lives in ./dashboardMappers — re-exported so
// consumers keep one import path.
export { ACTIVE_PIPELINE_STAGES, needsBuyerReview } from './dashboardMappers';
export type {
    DashboardRFQ, DashboardKpis, AttentionItem, RFQStage, OwnerView, StageFilter,
} from './dashboardMappers';

export const useDashboard = () => {
    const { backendUser } = useAuth();
    const myUserId = backendUser?.userId;
    const myEmail = backendUser?.email;

    const [rfqs, setRfqs] = useState<DashboardRFQ[]>([]);
    const [orgTotal, setOrgTotal] = useState(0);
    const [summary, setSummary] = useState<DashboardSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [ownerView, setOwnerView] = useState<OwnerView>('mine');
    const [stageFilter, setStageFilter] = useState<StageFilter>('all');
    // Once the user picks an owner view themselves, the auto-fallback stops.
    const ownerViewTouched = useRef(false);
    const pickOwnerView = useCallback((view: OwnerView) => {
        ownerViewTouched.current = true;
        setOwnerView(view);
    }, []);

    const fetchRFQs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            // Org-wide list (table rows) and org-wide summary (KPI/band
            // numbers) load together; a summary failure degrades the tiles,
            // not the table.
            const [response, summaryResult] = await Promise.all([
                trpClient.getRFQs(1, PAGE_LIMIT),
                dashboardClient.getSummary().catch((err) => {
                    console.error('Failed to fetch dashboard summary:', err);
                    return null;
                }),
            ]);
            setSummary(summaryResult?.success ? summaryResult.data : null);
            if (response.success) {
                setRfqs(response.data.items.map((rfq) => mapRfq(rfq, myUserId, myEmail)));
                setOrgTotal(response.data.pagination.total);
            }
        } catch (err: any) {
            console.error('Failed to fetch RFQs:', err);
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [myUserId, myEmail]);

    useEffect(() => {
        fetchRFQs();
    }, [fetchRFQs]);

    // The default scope is DERIVED, never configured: someone with active
    // RFQs of their own lands on them, someone without lands on the org's, so
    // nobody ever meets a table that is empty because of a filter. The server
    // count is exact; the fetched page is the fallback when the summary is
    // unreachable. The control shows the derived value, so org data never
    // renders under a "Mine" label — and an explicit click wins from then on.
    useEffect(() => {
        if (loading || ownerViewTouched.current) return;
        const hasOwnActive = summary
            ? summary.myActiveTotal > 0
            : rfqs.some((rfq) => rfq.isMine && isActiveRfq(rfq));
        setOwnerView(hasOwnActive ? 'mine' : 'everyone');
    }, [summary, rfqs, loading]);

    // The table is "Active RFQs" by definition — awarded/cancelled RFQs never
    // render here (owner, 28 Jul; they'll surface in a future RFQs section).
    const inOwnerView = useCallback(
        (rfq: DashboardRFQ) => ownerView === 'everyone' || rfq.isMine,
        [ownerView],
    );

    const filteredRfqs = useMemo(() => rfqs.filter(rfq => {
        if (!isActiveRfq(rfq) || !inOwnerView(rfq)) return false;
        if (stageFilter === 'all') return true;
        if (stageFilter === 'needs-review') return needsBuyerReview(rfq);
        return rfq.stage === stageFilter;
    }), [rfqs, inOwnerView, stageFilter]);

    const attentionItems = useMemo(() => deriveAttention(rfqs), [rfqs]);

    const stats = useMemo(() => {
        const activeRfqs = rfqs.filter(r => !r.awarded && r.status !== 'CANCELLED');
        const stageCounts = activeRfqs.reduce((acc, rfq) => {
            acc[rfq.stage] = (acc[rfq.stage] || 0) + 1;
            return acc;
        }, {} as Record<RFQStage, number>);

        const kpis: DashboardKpis = summary
            ? {
                activeTotal: summary.activeTotal,
                stagesInPlay: summary.stagesInPlay,
                awaitingReview: summary.awaitingReview,
                oldestWaitingDays: summary.oldestWaitingDays,
                suppliersEngaged: summary.suppliersEngaged,
                suppliersRespondedThisWeek: summary.suppliersRespondedThisWeek,
                avgCycleDays: summary.avgCycleDays,
                avgCycleDeltaPct: summary.avgCycleDeltaPct,
                completedCount: summary.completedCount,
                awardedThisMonth: summary.awardedThisMonth,
            }
            : deriveKpisFromPage(rfqs, stageCounts);

        const pipelineCounts: Record<string, number> = summary
            ? {
                'Upload BOM': summary.pipeline.uploadBom,
                'Sourcing Criteria': summary.pipeline.sourcingCriteria,
                'Supplier Discovery': summary.pipeline.supplierDiscovery,
                'Supplier Outreach': summary.pipeline.supplierOutreach,
                'Quotes & Proposals': summary.pipeline.quotesProposals,
            }
            : stageCounts;

        // Chip counts describe active rows inside the CURRENT owner view —
        // exactly what the table can show — so chips and table always agree.
        const inView = rfqs.filter(r => isActiveRfq(r) && inOwnerView(r));
        const filterCounts = {
            all: inView.length,
            needsReview: inView.filter(needsBuyerReview).length,
        };

        return {
            pipelineCounts,
            total: orgTotal,
            // "Has this person ever run an RFQ here" — the fact the new-person
            // welcome turns on. An award doesn't make someone new again, so
            // this counts every status, not just the active ones.
            myTotal: summary ? summary.myRfqTotal : rfqs.filter((rfq) => rfq.isMine).length,
            filterCounts,
            kpis,
        };
    }, [rfqs, summary, orgTotal, inOwnerView]);

    return {
        rfqs,
        filteredRfqs,
        attentionItems,
        loading,
        error,
        ownerView,
        setOwnerView: pickOwnerView,
        stageFilter,
        setStageFilter,
        refresh: fetchRFQs,
        stats,
    };
};
